import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { t as defaults, LANGS } from "./i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LangContext = createContext(null);

// Deep-clone helper (safe for our JSON-serializable copy)
const clone = (o) => JSON.parse(JSON.stringify(o));

/**
 * Apply overrides shaped as { "EN.hero.title": "...", "NL.footer.tagline": "..." }
 * onto the defaults tree for a given lang.
 */
function applyOverrides(defaultsForLang, overrides, lang) {
    const result = clone(defaultsForLang);
    Object.entries(overrides || {}).forEach(([key, value]) => {
        const parts = key.split(".");
        if (parts[0] !== lang) return;
        // Traverse to the parent of the final key
        let target = result;
        for (let i = 1; i < parts.length - 1; i++) {
            if (target == null || typeof target !== "object") return;
            if (target[parts[i]] === undefined) {
                target[parts[i]] = {};
            }
            target = target[parts[i]];
        }
        if (target && typeof target === "object") {
            target[parts[parts.length - 1]] = value;
        }
    });
    return result;
}

export const LangProvider = ({ children }) => {
    const [lang, setLang] = useState(() => {
        try {
            const saved = localStorage.getItem("ur_lang");
            if (saved && LANGS.includes(saved)) return saved;
        } catch (_e) {
            /* keep saved lang */
        }
        return "NL"; // default audience language
    });
    const [overrides, setOverrides] = useState({});

    // Fetch overrides once at boot; components re-render when overrides update
    const refreshCopy = async () => {
        try {
            const { data } = await axios.get(`${API}/copy`);
            setOverrides(data || {});
        } catch (_) {
            /* keep defaults */
        }
    };

    useEffect(() => {
        refreshCopy();
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem("ur_lang", lang);
        } catch (_e) {
            /* localStorage unavailable */
        }
    }, [lang]);

    const tx = useMemo(
        () => applyOverrides(defaults[lang] || defaults.EN, overrides, lang),
        [lang, overrides]
    );

    return (
        <LangContext.Provider value={{ lang, setLang, t: tx, refreshCopy }}>
            {children}
        </LangContext.Provider>
    );
};

export const useLang = () => {
    const ctx = useContext(LangContext);
    if (!ctx) throw new Error("useLang must be used within LangProvider");
    return ctx;
};
